import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Building2,
  Phone,
  MapPin,
  Users,
  Upload,
  X,
  Loader2,
  Calculator,
  Receipt,
  Download,
  Mail,
  Database,
} from "lucide-react";
import { toast } from "sonner";

const BusinessSettings = () => {
  const [settings, setSettings] = useState({
    business_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    office_hours: "",
    about_us: "",
    facebook_url: "",
    instagram_url: "",
    twitter_url: "",
    linkedin_url: "",
    logo_url: "",
    homepage_gallery: [],
    // Tax settings
    tax_paye_bracket1_limit: 4800,
    tax_paye_bracket2_limit: 6800,
    tax_paye_bracket3_limit: 8900,
    tax_paye_rate1: 20,
    tax_paye_rate2: 30,
    tax_paye_rate3: 37.5,
    tax_napsa_rate: 5,
    tax_napsa_cap: 1221.80,
    tax_nhima_rate: 1,
    tax_personal_levy: 3.00
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [emailingBackup, setEmailingBackup] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/business`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Failed to load business settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log("Saving settings:", settings);
      console.log("logo_url being sent:", settings.logo_url);
      const response = await axios.put(`${API}/settings/business`, settings, {
        withCredentials: true
      });
      console.log("Save response:", response.data);
      console.log("logo_url in response:", response.data?.logo_url);
      
      if (settings.logo_url && !response.data?.logo_url) {
        console.error("WARNING: logo_url was sent but not in response!");
        toast.warning("Logo may not have saved correctly. Please try again.");
      } else {
        toast.success("Business settings updated successfully!");
      }
      // Refresh settings to confirm save
      fetchSettings();
    } catch (error) {
      console.error("Failed to save settings:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log("Uploading logo...");
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      console.log("Upload response:", response.data);
      
      if (response.data.url) {
        console.log("Setting logo_url to:", response.data.url);
        handleChange("logo_url", response.data.url);
        toast.success("Logo uploaded! Click 'Save Changes' to apply.");
      } else {
        toast.error("Upload succeeded but no URL returned");
      }
    } catch (error) {
      console.error("Failed to upload logo:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleLogoUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleLogoUpload(e.target.files[0]);
    }
  };

  const removeLogo = () => {
    handleChange("logo_url", "");
    toast.success("Logo removed - click Save to apply");
  };

  // ============== Homepage Gallery ==============
  const [uploadingGallery, setUploadingGallery] = useState(false);

  const handleGalleryUpload = async (files) => {
    const fileArr = Array.from(files || []);
    if (fileArr.length === 0) return;

    const current = settings.homepage_gallery || [];
    const remaining = 10 - current.length;
    if (remaining <= 0) {
      toast.error("Gallery is full (10 max). Remove an image first.");
      return;
    }
    const toUpload = fileArr.slice(0, remaining);
    if (fileArr.length > remaining) {
      toast.warning(`Only uploading ${remaining} image(s) — gallery max is 10.`);
    }

    setUploadingGallery(true);
    const newUrls = [];
    for (const file of toUpload) {
      if (!file.type.startsWith("image/")) {
        toast.error(`Skipped non-image: ${file.name}`);
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`Skipped ${file.name} — over 8MB`);
        continue;
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await axios.post(`${API}/upload`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.url) newUrls.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      handleChange("homepage_gallery", [...current, ...newUrls]);
      toast.success(`Added ${newUrls.length} image(s). Click "Save Changes" to apply.`);
    }
    setUploadingGallery(false);
  };

  const removeGalleryImage = (idx) => {
    const next = (settings.homepage_gallery || []).filter((_, i) => i !== idx);
    handleChange("homepage_gallery", next);
    toast.success("Image removed - click Save to apply");
  };

  const moveGalleryImage = (idx, direction) => {
    const arr = [...(settings.homepage_gallery || [])];
    const target = idx + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    handleChange("homepage_gallery", arr);
  };

  // ============== Backup ==============
  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const res = await axios.get(`${API}/admin/backup/download`, {
        responseType: "blob",
      });
      // Trigger download
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `buyorent-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded! Save it to Google Drive or external storage.", { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download backup");
    } finally {
      setBackingUp(false);
    }
  };

  const emailBackupSummary = async () => {
    setEmailingBackup(true);
    try {
      const res = await axios.post(`${API}/admin/backup/email-summary`);
      toast.success(res.data?.message || "Summary emailed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to email summary");
    } finally {
      setEmailingBackup(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Settings</h1>
          <p className="text-gray-600">Manage your business contact information and branding</p>
          {settings._version && (
            <p className="text-xs text-gray-400 mt-1">API Version: {settings._version}</p>
          )}
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save Changes
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Basic Information
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
              <Input
                value={settings.business_name || ""}
                onChange={(e) => handleChange("business_name", e.target.value)}
                placeholder="Buyorent"
              />
            </div>
            
            {/* Logo Upload Section */}
            <div className="md:row-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Logo</label>
              <div 
                className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  dragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {settings.logo_url ? (
                  <div className="space-y-3">
                    <div className="relative inline-block">
                      <img 
                        src={settings.logo_url} 
                        alt="Business Logo" 
                        className="max-h-24 max-w-full mx-auto rounded-lg object-contain"
                      />
                      <button
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">Click or drag to replace</p>
                  </div>
                ) : (
                  <div className="space-y-2 py-4">
                    {uploadingLogo ? (
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-gray-400" />
                        <p className="text-sm text-gray-600">
                          <span className="text-primary font-medium">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                      </>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploadingLogo}
                />
              </div>
            </div>

            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">About Us</label>
              <Textarea
                value={settings.about_us || ""}
                onChange={(e) => handleChange("about_us", e.target.value)}
                placeholder="Brief description of your business..."
                rows={4}
              />
            </div>
          </div>
        </div>

        {/* Homepage Hero Gallery */}
        <div className="bg-white rounded-lg border p-6" data-testid="hero-gallery-section">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary" />
            Homepage Hero Gallery
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Up to 10 images. Visitors see them in a random rotating slideshow.
            Order can be adjusted with the arrows. {(settings.homepage_gallery || []).length}/10 images.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
            {(settings.homepage_gallery || []).map((url, idx) => (
              <div
                key={`${url}-${idx}`}
                className="relative group rounded-lg overflow-hidden border aspect-video"
                data-testid={`gallery-item-${idx}`}
              >
                <img src={url} alt={`Hero ${idx + 1}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={() => moveGalleryImage(idx, -1)}
                      className="bg-white/90 hover:bg-white rounded p-1 text-xs"
                      title="Move left"
                      data-testid={`gallery-move-left-${idx}`}
                    >
                      ←
                    </button>
                  )}
                  {idx < (settings.homepage_gallery || []).length - 1 && (
                    <button
                      type="button"
                      onClick={() => moveGalleryImage(idx, 1)}
                      className="bg-white/90 hover:bg-white rounded p-1 text-xs"
                      title="Move right"
                      data-testid={`gallery-move-right-${idx}`}
                    >
                      →
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(idx)}
                    className="bg-red-500 hover:bg-red-600 text-white rounded p-1"
                    title="Remove"
                    data-testid={`gallery-remove-${idx}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <span className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                  {idx + 1}
                </span>
              </div>
            ))}

            {(settings.homepage_gallery || []).length < 10 && (
              <label
                className={`relative border-2 border-dashed rounded-lg aspect-video flex flex-col items-center justify-center cursor-pointer transition-colors ${
                  uploadingGallery ? "border-primary bg-primary/5" : "border-gray-300 hover:border-primary/50"
                }`}
                data-testid="gallery-upload-tile"
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleGalleryUpload(e.target.files)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploadingGallery}
                />
                {uploadingGallery ? (
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-xs text-gray-500 mt-1">Add images</span>
                  </>
                )}
              </label>
            )}
          </div>

          {(settings.homepage_gallery || []).length === 0 && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              No gallery images uploaded yet. The homepage will show a default image until you add some.
            </div>
          )}
        </div>

        {/* Backup */}
        <div className="bg-white rounded-lg border p-6" data-testid="backup-section">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Data Backup
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Download a complete snapshot of all your business data (users, properties, rentals, payroll, accounting, etc.) as a single JSON file. Store it in Google Drive, Dropbox, or an external drive for safekeeping.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800 mb-4">
            ⚠️ <strong>Recommended:</strong> Download a backup at least weekly. An automatic reminder will be emailed to admins every Monday at 9 AM UTC.
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={downloadBackup} disabled={backingUp} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-backup-btn">
              {backingUp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download Backup Now
            </Button>
            <Button variant="outline" onClick={emailBackupSummary} disabled={emailingBackup} data-testid="email-summary-btn">
              {emailingBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Email Me a Summary
            </Button>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            Contact Information
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <Input
                type="email"
                value={settings.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="info@buyorent.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <Input
                value={settings.phone || ""}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="+260 960911896"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office Hours</label>
              <Input
                value={settings.office_hours || ""}
                onChange={(e) => handleChange("office_hours", e.target.value)}
                placeholder="Mon - Fri, 8:00 AM - 5:00 PM"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Business Address
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <Input
                value={settings.address || ""}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="69F Hilltop View"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <Input
                value={settings.city || ""}
                onChange={(e) => handleChange("city", e.target.value)}
                placeholder="Mufulira"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
              <Input
                value={settings.state || ""}
                onChange={(e) => handleChange("state", e.target.value)}
                placeholder="Copperbelt"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <Input
                value={settings.country || ""}
                onChange={(e) => handleChange("country", e.target.value)}
                placeholder="Zambia"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Social Media Links
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
              <Input
                value={settings.facebook_url || ""}
                onChange={(e) => handleChange("facebook_url", e.target.value)}
                placeholder="https://facebook.com/yourpage"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
              <Input
                value={settings.instagram_url || ""}
                onChange={(e) => handleChange("instagram_url", e.target.value)}
                placeholder="https://instagram.com/yourpage"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Twitter / X</label>
              <Input
                value={settings.twitter_url || ""}
                onChange={(e) => handleChange("twitter_url", e.target.value)}
                placeholder="https://twitter.com/yourpage"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
              <Input
                value={settings.linkedin_url || ""}
                onChange={(e) => handleChange("linkedin_url", e.target.value)}
                placeholder="https://linkedin.com/company/yourpage"
              />
            </div>
          </div>
        </div>

        {/* Calculator Settings */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            Mortgage Calculator Defaults
          </h2>
          <p className="text-sm text-gray-500 mb-4">Set the default values users see when they open the calculator.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Interest Rate (%)</label>
              <Input
                type="number"
                step="0.125"
                value={settings.calc_default_interest_rate ?? 6.5}
                onChange={(e) => handleChange("calc_default_interest_rate", parseFloat(e.target.value) || 0)}
                data-testid="calc-interest-rate"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Down Payment (%)</label>
              <Input
                type="number"
                step="1"
                value={settings.calc_default_down_payment_pct ?? 20}
                onChange={(e) => handleChange("calc_default_down_payment_pct", parseFloat(e.target.value) || 0)}
                data-testid="calc-down-payment"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Annual Property Tax (K)</label>
              <Input
                type="number"
                value={settings.calc_default_property_tax ?? 2500}
                onChange={(e) => handleChange("calc_default_property_tax", parseFloat(e.target.value) || 0)}
                data-testid="calc-property-tax"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Annual Insurance (K)</label>
              <Input
                type="number"
                value={settings.calc_default_insurance ?? 1200}
                onChange={(e) => handleChange("calc_default_insurance", parseFloat(e.target.value) || 0)}
                data-testid="calc-insurance"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Home Price (K)</label>
              <Input
                type="number"
                value={settings.calc_min_home_price ?? 100000}
                onChange={(e) => handleChange("calc_min_home_price", parseFloat(e.target.value) || 0)}
                data-testid="calc-min-price"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Home Price (K)</label>
              <Input
                type="number"
                value={settings.calc_max_home_price ?? 5000000}
                onChange={(e) => handleChange("calc_max_home_price", parseFloat(e.target.value) || 0)}
                data-testid="calc-max-price"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Terms (comma-separated years)</label>
              <Input
                value={settings.calc_loan_terms ?? "15,20,30"}
                onChange={(e) => handleChange("calc_loan_terms", e.target.value)}
                placeholder="15,20,30"
                data-testid="calc-loan-terms"
              />
              <p className="text-xs text-gray-400 mt-1">e.g. 10,15,20,25,30</p>
            </div>
          </div>
        </div>

        {/* Tax & Statutory Deductions Settings */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            Tax & Statutory Deductions (Zambia)
          </h2>
          <p className="text-sm text-gray-500 mb-4">Configure PAYE brackets, NAPSA, NHIMA, and Personal Levy rates for payroll calculations.</p>
          
          {/* PAYE Brackets */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 border-b pb-2">PAYE Tax Brackets (Monthly)</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bracket 1 Limit (K)</label>
                <Input
                  type="number"
                  value={settings.tax_paye_bracket1_limit ?? 4800}
                  onChange={(e) => handleChange("tax_paye_bracket1_limit", parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-gray-400 mt-1">0% tax up to this amount</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bracket 2 Limit (K)</label>
                <Input
                  type="number"
                  value={settings.tax_paye_bracket2_limit ?? 6800}
                  onChange={(e) => handleChange("tax_paye_bracket2_limit", parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-gray-400 mt-1">Rate 1 applies between B1 and B2</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bracket 3 Limit (K)</label>
                <Input
                  type="number"
                  value={settings.tax_paye_bracket3_limit ?? 8900}
                  onChange={(e) => handleChange("tax_paye_bracket3_limit", parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-gray-400 mt-1">Rate 2 applies between B2 and B3</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rate 1 (%)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.tax_paye_rate1 ?? 20}
                  onChange={(e) => handleChange("tax_paye_rate1", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rate 2 (%)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.tax_paye_rate2 ?? 30}
                  onChange={(e) => handleChange("tax_paye_rate2", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rate 3 (%) - Above B3</label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.tax_paye_rate3 ?? 37.5}
                  onChange={(e) => handleChange("tax_paye_rate3", parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>

          {/* NAPSA, NHIMA, Personal Levy */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3 border-b pb-2">Other Statutory Deductions</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">NAPSA Rate (%)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.tax_napsa_rate ?? 5}
                  onChange={(e) => handleChange("tax_napsa_rate", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">NAPSA Cap (K)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.tax_napsa_cap ?? 1221.80}
                  onChange={(e) => handleChange("tax_napsa_cap", parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-gray-400 mt-1">Maximum monthly contribution</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">NHIMA Rate (%)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.tax_nhima_rate ?? 1}
                  onChange={(e) => handleChange("tax_nhima_rate", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Personal Levy (K)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.tax_personal_levy ?? 3.00}
                  onChange={(e) => handleChange("tax_personal_levy", parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-gray-400 mt-1">Fixed monthly amount</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessSettings;
